import type { DocPage } from './types';

/**
 * The lifecycle page, and the derived audit checklist.
 *
 * The checklist has no hand-written rows. It reads every page's `status`, so a
 * feature cannot be described as finished on its own page while showing as
 * incomplete here, or the reverse — the two cannot disagree because there is
 * only one source.
 */
export const CONCEPT_PAGES: DocPage[] = [
  {
    slug: 'approval-lifecycle',
    title: 'Approval lifecycle',
    summary: 'The six states an action moves through, and which of them are final.',
    section: 'concepts',
    keywords: ['states', 'lifecycle', 'pending', 'executed', 'expired', 'failed', 'rejected'],
    blocks: [
      {
        kind: 'table',
        headers: ['State', 'Meaning', 'Final?'],
        rows: [
          ['pending', 'Waiting on a human decision.', 'No'],
          ['approved', 'Signed off. Execution starts immediately.', 'No'],
          ['executed', 'Ran successfully through the connector.', 'Yes'],
          ['rejected', 'A human said no.', 'Yes'],
          ['expired', 'Nobody answered before the deadline. Nothing ran.', 'Yes'],
          ['failed', 'Approved, but the tool call failed.', 'Yes'],
        ],
      },
      {
        kind: 'text',
        body: 'Legal moves are exhaustive and enumerated: pending may become approved, rejected or expired; approved may become executed or failed. Everything else is refused, so an accidental path such as rejected → approved cannot be introduced.',
      },
      {
        kind: 'note',
        tone: 'info',
        body: 'The lifecycle check runs inside a database transaction against the record as it is at that instant. If two processes race an approved → executed change, the loser re-reads the winner’s result and is refused rather than overwriting it.',
      },
      { kind: 'heading', body: 'Why an expiry is a rejection' },
      {
        kind: 'text',
        body: 'An unattended action fails closed. If a timeout approved instead, the queue would become an approval anyone could win by waiting — which is the opposite of what an approval gateway is for.',
      },
      { kind: 'heading', body: 'Executing exactly once' },
      {
        kind: 'text',
        body: 'Two things stop an action running twice. The idempotency key is claimed in the same transaction that creates the request, so a retried webhook can never produce a second request. And before the connector is touched, the executing process takes a lease on the approval — so an auto-approval, a double-clicked button and two expiry sweeps cannot all reach the tool.',
      },
      {
        kind: 'note',
        tone: 'warn',
        body: 'The lease lasts two minutes. If a connector call genuinely outruns that, a second execution becomes possible again. Closing that completely means forwarding idempotency keys down into each connector, which is not done yet.',
      },
    ],
  },

  // -------------------------------------------------------------------------
  {
    slug: 'audit-checklist',
    title: 'Developer audit checklist',
    summary:
      'Generated from every documented feature’s status. Nothing here is written by hand, so it cannot disagree with the feature pages.',
    section: 'reference',
    keywords: ['audit', 'checklist', 'status', 'incomplete', 'todo', 'gaps', 'coverage'],
    blocks: [
      {
        kind: 'note',
        tone: 'info',
        body: 'This page renders itself from the status attached to each documented feature. To change a row, change that feature’s page — there is no separate list to keep in sync.',
      },
      {
        kind: 'text',
        body: 'Columns are the questions asked of every feature: does it exist, is the frontend done, is the backend done, does it persist, is it authenticated, are permissions enforced and tested, is failure handled, and is there automated coverage.',
      },
    ],
  },
];
